import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UserAddress } from '../../../../core/models/user-address';
import { AddAddressRequest } from '../../../../core/requests/AddAddressRequest';
import { AuthenticationService, UserService } from '../../../../core/service';
import { Patch } from '../../../../core/models/patch';
import { TranslatePipe } from '@ngx-translate/core';
import { CoreTranslationService } from '../../../../../@core/services/translation.service';
import {AddressesType, CountyLabel, CountyMap} from '../../../../core/helpers/constants';
import { ToastrService } from 'ngx-toastr';
import {County} from "../../../../core/helpers/enums";

@Component({
  selector: 'app-add-address',
  templateUrl: './add-address.component.html',
  styleUrls: ['./add-address.component.scss'],
  providers: [
    TranslatePipe
  ]
})
export class AddAddressComponent implements OnInit {

  @Input() public selectedAddress: UserAddress | null;
  @Input() public actionType: string;
  @Input() public addressType: string;
  public addAddressForm: FormGroup | null = null;
  // Visibility toggle for the masked contact phone number. Defaults to
  // visible since the value is freshly typed in; only masked by default
  // when editing an existing (pre-filled) address.
  public showPhone = true;

  public countyLabels = Object.keys(CountyLabel)
    .filter((key) => Number(key) !== County.WholeCountry)
    .map((key) => {
      const enumValue = Number(key) as County;
      return {
        name: CountyLabel[enumValue],
        value: enumValue
      }
  });

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private _coreTranslationService: CoreTranslationService,
    private userService: UserService,
    private translate: TranslatePipe,
    private authenticationService: AuthenticationService,
    private _toastrService: ToastrService
  ) {
    // this._coreTranslationService.translate(english, romanian);
  }

  ngOnInit(): void {
    this.addAddressForm = new FormGroup({
      name: new FormControl(
        this.authenticationService.currentUserValue.lastName + ' ' +
                this.authenticationService.currentUserValue.firstName,
        [Validators.required]),
      phoneNumber: new FormControl(this.authenticationService.currentUserValue.phoneNumber, [Validators.required, Validators.pattern('^[0-9]{10}$')]),
      county: new FormControl(null, [Validators.required]),
      city: new FormControl('', [Validators.required]),
      street: new FormControl('', [Validators.required]),
      number: new FormControl('', [Validators.required]),
      zipCode: new FormControl('', [Validators.required]),
      building: new FormControl('', []),
      staircase: new FormControl('', []),
      floor: new FormControl('', []),
      useSameForBilling: new FormControl(false, []),
      apartment: new FormControl('', [])
    });
    if (this.actionType === 'edit') {
      this.showPhone = false;
      this.addAddressForm.patchValue({
        'name': this.selectedAddress.contactPersonName,
        'phoneNumber': this.selectedAddress.contactPersonPhoneNumber,
        'county': this.mapCountyNameToEnum(this.selectedAddress.county),
        'city': this.selectedAddress.city,
        'street': this.selectedAddress.street,
        'number': this.selectedAddress.number,
        'zipCode': this.selectedAddress.zipCode,
        'building': this.selectedAddress.building,
        'staircase': this.selectedAddress.staircase,
        'floor': this.selectedAddress.floor,
        'apartment': this.selectedAddress.apartment
      });
    }
  }

  addAddress() {
    if (this.actionType === 'edit') {
      const params: any = {
        contactPersonName: this.addAddressForm.get('name')?.value,
        contactPersonPhoneNumber: this.addAddressForm.get('phoneNumber')?.value,
        name: this.addAddressForm.get('name')?.value,
        phoneNumber: this.addAddressForm.get('phoneNumber')?.value,
        county: this.translate.transform(CountyLabel[this.addAddressForm.get('county')?.value]),
        city: this.addAddressForm.get('city')?.value,
        street: this.addAddressForm.get('street')?.value,
        number: this.addAddressForm.get('number')?.value,
        zipCode: this.addAddressForm.get('zipCode')?.value,
        building: this.addAddressForm.get('building')?.value,
        staircase: this.addAddressForm.get('staircase')?.value,
        floor: this.addAddressForm.get('floor')?.value,
        apartment: this.addAddressForm.get('apartment')?.value,
      };
      const patchData: Array<Patch> = Object.keys(this.selectedAddress).filter(key => params[key] && this.selectedAddress[key] !== params[key]).map(key => {
        this.selectedAddress[key] = params[key];
        return {
          op: 'replace',
          path: key,
          value: params[key]
        };
      });
      this.userService.editUserAddress(patchData, this.selectedAddress.id)
        .subscribe(
          _ => {
            this.onEditAddressSuccess();
          },
          error => {
            this.onEditAddAddressFail(error);
          });
    } else {
      const params: AddAddressRequest = {
        contactPersonName: this.addAddressForm.get('name')?.value,
        contactPersonPhoneNumber: this.addAddressForm.get('phoneNumber')?.value,
        county: this.translate.transform(CountyLabel[this.addAddressForm.get('county')?.value]),
        city: this.addAddressForm.get('city')?.value,
        street: this.addAddressForm.get('street')?.value,
        number: this.addAddressForm.get('number')?.value,
        zipCode: this.addAddressForm.get('zipCode')?.value,
        building: this.addAddressForm.get('building')?.value,
        staircase: this.addAddressForm.get('staircase')?.value,
        floor: this.addAddressForm.get('floor')?.value,
        apartment: this.addAddressForm.get('apartment')?.value,
        userId: this.authenticationService.currentUserValue.userId,
        isDeleted: false,
        addressType: (this.addressType === 'billing') ? AddressesType.Billing : AddressesType.Shipping
      };
      this.userService.addUserAddress(params)
        .subscribe(
          _ => {
            this.onAddAddressSuccess();
          },
          error => {
            this.onEditAddAddressFail(error);
          });
    }
  }

  public onEditAddressSuccess() {
    this._toastrService.success(
      this.translate.transform('ADDRESSES.EDIT_SUCCESS'),
      this.translate.transform('SUCCESS'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
    this.activeModal.close([true, this.selectedAddress]);
  }

  public onAddAddressSuccess() {
    if (this.addAddressForm.get('useSameForBilling')?.value && this.addressType !== 'billing') {
      const params: AddAddressRequest = {
        contactPersonName: this.addAddressForm.get('name')?.value,
        contactPersonPhoneNumber: this.addAddressForm.get('phoneNumber')?.value,
        county: this.addAddressForm.get('county')?.value,
        city: this.addAddressForm.get('city')?.value,
        street: this.addAddressForm.get('street')?.value,
        number: this.addAddressForm.get('number')?.value,
        zipCode: this.addAddressForm.get('zipCode')?.value,
        building: this.addAddressForm.get('building')?.value,
        staircase: this.addAddressForm.get('staircase')?.value,
        floor: this.addAddressForm.get('floor')?.value,
        apartment: this.addAddressForm.get('apartment')?.value,
        userId: this.authenticationService.currentUserValue.userId,
        isDeleted: false,
        addressType: AddressesType.Billing
      };
      this.userService.addUserAddress(params)
        .subscribe(
          _ => {
          },
          error => {
          });
      this.activeModal.close([true, null]);
    } else {
      this.activeModal.close([true, null]);
    }

  }

  public onEditAddAddressFail(error: any) {
    error.messages.forEach((errorItem) => {
      this._toastrService.error(
        this.translate.transform(errorItem),
        this.translate.transform('ERROR'),
        { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
      );
    });
    this.activeModal.close([false, null]);
  }

  public closeBtnClick() {
    this.activeModal.close([false, null]);
  }

  public getFormValidationErrors() {
    let totalErrors = 0;

    Object.keys(this.addAddressForm.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.addAddressForm.get(key).errors;
      if (controlErrors != null) {
        totalErrors++;
      }
    });
  }

  customSearchFn = (term: string, item: { name: string; value: string }) => {
    const translated = this.translate.transform(item.name);
    return translated.toLowerCase().includes(term.toLowerCase());
  };

   private mapCountyNameToEnum(countyName: string): County | null {
    if (!countyName) return null;
    const normalized = countyName.trim().toLowerCase();
    return CountyMap[normalized] ?? null;
  }
}
